import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortalOverviewService } from '../../services/portal/portalOverviewService';
import { useClientPortal } from '../../context/ClientPortalContext';
import ClientStatCard from '../../components/client/dashboard/ClientStatCard';
import ApplicationTimeline from '../../components/client/dashboard/ApplicationTimeline';
import DeadlineCountdown from '../../components/client/dashboard/DeadlineCountdown';
import QuickActions from '../../components/client/dashboard/QuickActions';
import ProfileCompletionCard from '../../components/client/dashboard/ProfileCompletionCard';
import {
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const TasksList = ({ tasks, onTaskClick }) => {
  if (!tasks?.length) {
    return (
      <div className="text-center py-6">
        <span className="text-3xl mb-2 block">🎯</span>
        <p className="text-sm text-slate-500">No pending tasks</p>
        <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {tasks.slice(0, 4).map((task) => (
        <li
          key={task.id}
          onClick={() => onTaskClick?.(task)}
          className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
              <ClipboardDocumentListIcon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{task.title}</p>
              <p className="text-xs text-slate-500">Due {formatDate(task.due_date)}</p>
            </div>
          </div>
          <span className={`
                        text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-full
                        ${task.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}
                    `}>
            {task.status}
          </span>
        </li>
      ))}
      {tasks.length > 4 && (
        <p className="text-xs text-slate-500 text-center py-1">+{tasks.length - 4} more tasks</p>
      )}
    </ul>
  );
};

const AgreementsList = ({ agreements }) => {
  if (!agreements?.length) {
    return <p className="text-sm text-slate-500">All agreements are up to date.</p>;
  }

  return (
    <ul className="space-y-3">
      {agreements.slice(0, 3).map((agreement) => (
        <li key={agreement.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
              <DocumentTextIcon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{agreement.title || 'Engagement Agreement'}</p>
              <p className="text-xs text-slate-500">Sent {formatDate(agreement.sent_at)}</p>
            </div>
          </div>
          <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2 py-1 rounded-full">{agreement.status}</span>
        </li>
      ))}
    </ul>
  );
};

const InvoicesList = ({ invoices }) => {
  if (!invoices?.length) {
    return <p className="text-sm text-slate-500">No outstanding invoices.</p>;
  }

  return (
    <ul className="space-y-3">
      {invoices.slice(0, 3).map((invoice) => (
        <li key={invoice.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <div>
            <p className="text-sm font-medium text-slate-900">Invoice {invoice.invoice_number || invoice.id.slice(0, 6)}</p>
            <p className="text-xs text-slate-500">Due {formatDate(invoice.due_date)}</p>
          </div>
          <p className="text-sm font-semibold text-rose-600">
            {invoice.currency || 'USD'} {Number(invoice.amount_due || 0).toLocaleString()}
          </p>
        </li>
      ))}
    </ul>
  );
};

const Section = ({ title, description, children, className = '' }) => (
  <section className={`bg-white border border-slate-200 rounded-2xl p-5 shadow-sm ${className}`}>
    <div className="mb-4">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
    {children}
  </section>
);

const DocumentProgress = ({ documentStatus }) => {
  const total = (documentStatus?.required ?? 0);
  const submitted = (documentStatus?.submitted ?? 0);
  const percentage = total > 0 ? Math.round((submitted / total) * 100) : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-slate-900 mb-4">Document Progress</h3>

      <div className="flex items-center gap-6">
        {/* Progress Ring */}
        <div className="relative h-20 w-20 flex-shrink-0">
          <svg className="h-20 w-20 transform -rotate-90">
            <circle
              cx="40"
              cy="40"
              r="36"
              stroke="currentColor"
              strokeWidth="6"
              fill="none"
              className="text-slate-100"
            />
            <circle
              cx="40"
              cy="40"
              r="36"
              stroke="currentColor"
              strokeWidth="6"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 36}`}
              strokeDashoffset={`${2 * Math.PI * 36 * (1 - percentage / 100)}`}
              className="text-indigo-500 transition-all duration-700"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-indigo-600">{percentage}%</span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex-1 grid grid-cols-3 gap-3">
          <div className="text-center p-2 bg-indigo-50 rounded-xl">
            <p className="text-lg font-bold text-indigo-600">{documentStatus?.required ?? 0}</p>
            <p className="text-xs text-slate-500">Required</p>
          </div>
          <div className="text-center p-2 bg-emerald-50 rounded-xl">
            <p className="text-lg font-bold text-emerald-600">{documentStatus?.submitted ?? 0}</p>
            <p className="text-xs text-slate-500">Submitted</p>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded-xl">
            <p className="text-lg font-bold text-amber-600">{documentStatus?.pendingReview ?? 0}</p>
            <p className="text-xs text-slate-500">Pending</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDashboardPage = () => {
  const navigate = useNavigate();
  const { client } = useClientPortal();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const greeting = useMemo(() => getGreeting(), []);

  useEffect(() => {
    let mounted = true;
    if (!client?.id) {
      setOverview(null);
      setLoading(false);
      return () => { mounted = false; };
    }
    setLoading(true);
    PortalOverviewService.fetchOverview(client).then((result) => {
      if (mounted) {
        setOverview(result);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [client]);

  const handleQuickAction = (action) => {
    if (action.path) navigate(action.path);
  };

  const handleDeadlineClick = (deadline) => {
    if (deadline.type === 'application' || deadline.type === 'university_application') {
      navigate(`/portal/applications/${deadline.id}`);
    } else if (deadline.type === 'document') {
      navigate('/portal/documents');
    }
  };

  // Transform pending tasks and deadlines for the countdown
  const deadlines = useMemo(() => {
    const items = [];

    // Add tasks with due dates
    (overview?.pendingTasks || []).forEach(task => {
      if (task.due_date) {
        items.push({
          id: task.id,
          title: task.title,
          type: 'task',
          date: new Date(task.due_date),
          subtitle: 'Task'
        });
      }
    });

    // Add agreements
    (overview?.agreementsDue || []).forEach(agreement => {
      items.push({
        id: agreement.id,
        title: agreement.title || 'Agreement',
        type: 'agreement',
        date: new Date(agreement.sent_at || Date.now()),
        subtitle: 'Needs signature'
      });
    });

    return items.sort((a, b) => (a.date || 0) - (b.date || 0));
  }, [overview]);

  // Determine current application stage
  const currentStage = useMemo(() => {
    const stage = overview?.primaryCase?.current_stage?.toLowerCase();
    if (!stage) return 'submitted';
    if (stage.includes('submit')) return 'submitted';
    if (stage.includes('process') || stage.includes('review')) return 'processing';
    if (stage.includes('decision') || stage.includes('wait')) return 'decision';
    if (stage.includes('offer') || stage.includes('accept')) return 'offer';
    if (stage.includes('visa')) return 'visa';
    if (stage.includes('ready') || stage.includes('depart')) return 'ready';
    return 'processing';
  }, [overview]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-20 bg-slate-100 rounded-2xl animate-pulse"></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              {greeting}, {client?.first_name || 'there'}! 👋
            </h1>
            <p className="text-indigo-100 mt-1 text-sm">
              Here's an overview of your migration journey
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 bg-white/10 backdrop-blur rounded-xl px-4 py-2">
            <CalendarDaysIcon className="h-5 w-5" />
            <span className="text-sm font-medium">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ClientStatCard
          title="Active Case"
          value={overview?.primaryCase?.current_stage || 'Not assigned'}
          icon={<AcademicCapIcon className="h-6 w-6" />}
          color="indigo"
          onClick={() => navigate('/portal/applications')}
        />
        <ClientStatCard
          title="Pending Tasks"
          value={overview?.pendingTasks?.length || 0}
          icon={<ClipboardDocumentListIcon className="h-6 w-6" />}
          color="amber"
          pulse={overview?.pendingTasks?.length > 3}
        />
        <ClientStatCard
          title="Agreements Due"
          value={overview?.agreementsDue?.length || 0}
          icon={<DocumentCheckIcon className="h-6 w-6" />}
          color="rose"
          onClick={() => navigate('/portal/agreements')}
        />
        <ClientStatCard
          title="Unread Messages"
          value={overview?.unreadMessages || 0}
          icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
          color="purple"
          onClick={() => navigate('/portal/messages')}
        />
      </div>

      {/* Application Timeline */}
      <ApplicationTimeline
        currentStage={currentStage}
        application={{
          university: overview?.primaryCase?.title || 'Your Application'
        }}
      />

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Tasks & Deadlines */}
        <div className="lg:col-span-2 space-y-6">
          <Section
            title="Next Steps"
            description="Tasks from your consultant - complete them to keep momentum"
          >
            <TasksList tasks={overview?.pendingTasks} />
          </Section>

          <div className="grid gap-6 sm:grid-cols-2">
            <Section title="Agreements Awaiting Action">
              <AgreementsList agreements={overview?.agreementsDue} />
            </Section>

            <Section title="Outstanding Invoices">
              <InvoicesList invoices={overview?.invoicesDue} />
            </Section>
          </div>

          <DocumentProgress documentStatus={overview?.documentStatus} />
        </div>

        {/* Right Column - Profile, Deadlines & Quick Actions */}
        <div className="space-y-6">
          <ProfileCompletionCard />
          <DeadlineCountdown
            deadlines={deadlines}
            onItemClick={handleDeadlineClick}
            loading={loading}
          />
          <QuickActions onAction={handleQuickAction} />
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardPage;
